import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import type { BreakdownRow } from '../../hooks/useAnalytics'

/**
 * One grouped breakdown. Every number is a server-side aggregate over the whole
 * filtered range, not a client-side sum of a truncated page.
 */
export interface BreakdownTableProps {
  rows: BreakdownRow[]
  loading: boolean
  keyHeader: string
  subHeader?: string
  labelFor?: (key: string | number | null) => string
  subLabelFor?: (key: string | number | null) => string
  onSelect?: (key: string | number | null) => void
  /** Show the API error-rate column (meaningless for non-API dimensions). */
  showErrors?: boolean
  emptyMessage?: string
}

function relative(ts: number | null): string {
  if (!ts) return '—'
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return `${(days / 30.44).toFixed(1)}mo ago`
}

export function BreakdownTable({
  rows,
  loading,
  keyHeader,
  subHeader,
  labelFor = k => (k === null || k === '' ? 'Unattributed' : String(k)),
  subLabelFor = k => (k === null || k === '' ? '—' : String(k)),
  onSelect,
  showErrors = false,
  emptyMessage = 'No activity in this range',
}: BreakdownTableProps) {
  if (loading && rows.length === 0) {
    return <div className="p-5 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
  }
  if (rows.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
  }

  const max = Math.max(...rows.map(r => r.interactions ?? r.events), 1)

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{keyHeader}</TableHead>
          {subHeader && <TableHead>{subHeader}</TableHead>}
          <TableHead className="text-right">Interactions</TableHead>
          <TableHead className="text-right">Views</TableHead>
          <TableHead className="text-right">API</TableHead>
          <TableHead className="text-right">All events</TableHead>
          {showErrors && <TableHead className="text-right">Errors</TableHead>}
          <TableHead className="text-right">Users</TableHead>
          <TableHead className="text-right">Last seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow
            key={`${row.key}-${row.subKey}-${i}`}
            className={onSelect ? 'cursor-pointer' : undefined}
            onClick={onSelect ? () => onSelect(row.key) : undefined}
          >
            <TableCell className="font-medium text-foreground">
              <div className="flex items-center gap-2">
                {/* Inline magnitude bar — cheaper to read than the raw number. */}
                <span className="relative block h-1.5 w-16 shrink-0 rounded-full bg-muted">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full bg-seed-green-500"
                    style={{ width: `${((row.interactions ?? row.events) / max) * 100}%` }}
                  />
                </span>
                <span className="truncate">{labelFor(row.key)}</span>
              </div>
            </TableCell>
            {subHeader && <TableCell className="text-muted-foreground">{subLabelFor(row.subKey)}</TableCell>}
            <TableCell className="text-right tabular-nums">{(row.interactions ?? 0).toLocaleString()}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{row.navViews.toLocaleString()}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{row.apiCalls.toLocaleString()}</TableCell>
            {/* Kept, but demoted: it is the request count, not the activity. */}
            <TableCell className="text-right tabular-nums text-muted-foreground">{row.events.toLocaleString()}</TableCell>
            {showErrors && (
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {/* Rate over calls with a RECORDED status — backfilled rows have
                    none, so an all-historical window shows a dash, not 0%. */}
                {row.measured > 0 ? `${((row.errors / row.measured) * 100).toFixed(1)}%` : '—'}
              </TableCell>
            )}
            <TableCell className="text-right tabular-nums text-muted-foreground">{row.uniqueUsers.toLocaleString()}</TableCell>
            <TableCell className="text-right whitespace-nowrap text-muted-foreground">{relative(row.lastSeen)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
